<template>
  <section>
    <div class="content-wrapper">
      <errors v-if="isVisibleErrorsModal"></errors>
      <div class="content-heading">
        <div v-if="accountType === 'joint'">Create account / Joint</div>
        <div v-if="accountType === 'utma-ugma'">Create account / UTMA / UGMA</div>
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <button type="button" class="btn-back icon-back bottom-margin" v-on:click="goBack()">Go back</button>

      <div class="row">
        <spinner v-if="isLoading"></spinner>
        <div class="col-lg-8">
          <div id="panelChart9" class="panel panel-default">
            <div class="panel-title">
              <div v-if="accountType === 'joint'">Joint account information</div>
              <div v-if="accountType === 'utma-ugma'">UTMA / UGMA information</div>
            </div>
            <div class="line-separator"></div>
            <div class="space" />
              <div class="row top-margin">
                <div class="col-md-5">
                  <div>
                    <label>Account name*</label>
                  </div>
                </div>
              </div>
              <div class="row">
                  <div class="col-md-5">
                    <div>
                      <RestrictedSingleInput
                        @inputUpdated="onAccountNameUpdate"
                        :input="accountName"
                        :limit="40"
                        :placeholderText="'Account name'"
                      />
                      <span
                        v-if="accountNameChanged && accountName.trim().length === 0"
                        class="error-feedback"
                      >Required</span>
                    </div>
                  </div>
                </div>
              <div class="space" />
              <div class="row top-margin">
                <div class="col-md-5">
                  <div>
                    <label>Principal maturity preference*</label>
                  </div>
                </div>
                <div class="col-md-5 left-margin">
                  <div>
                    <label>Interest payment preference*</label>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-5">
                  <div class="form-group">
                    <select v-model="principalPaymentSelected" name="principalPayment" id="principalPayment" required
                      :class="{ 'form-control': true, 'form-control-error': errors.has('principalPayment') }" v-validate="{required: true}">
                      <option value="" disabled selected hidden>Select principal payment preference</option>
                        <option v-for="principal in paymentPreference" :value="principal.name">{{principal.text}}</option>
                    </select>
                    <span v-if="errors.has('principalPayment')" class="error-feedback">Required</span>
                  </div>
                </div>
                <div class="col-md-5 left-margin">
                  <div class="form-group">
                    <select v-model="interestPaymentSelected" name="interestPayment" id="interestPayment" required
                      :class="{ 'form-control': true, 'form-control-error': errors.has('interestPayment') }" v-validate="{required: true}">
                      <option value="" disabled selected hidden>Select interest payment preference</option>
                        <option v-for="interest in paymentPreference" :value="interest.name">{{interest.text}}</option>
                    </select>
                    <span v-if="errors.has('interestPayment')" class="error-feedback">Required</span>
                  </div>
                </div>
              </div>
              <div class="space" />
              <div class="row top-margin">
                <div class="col-md-10">
                    <button type="submit" class="btn btn-info btn-lg button-approve" v-bind:disabled="isSubmitButtonDisabled()" v-on:click="submitRequest()">Submit</button>
                </div>
              </div>
              <div class="line-separator top-margin"></div>
              <button type="button" class="btn-back icon-back bottom-margin" v-on:click="goBack()">Go back</button>
              <div class="space" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import {
    mapActions,
    mapMutations,
    mapState,
  } from 'vuex';

  import Errors from '@/components/modals/Errors';
  import paymentPreferenceAction from '@/data/static/upcomingTransactionActions'; // eslint-disable-line no-unused-vars
  import { UserTypes } from '@/data/static/userTypes';
  import ProhibitedStates from '@/data/static/reinvestProhibitedStates';
  import RestrictedSingleInput from '@/components/formControls/RestrictedSingleInput';

  import {
    SET_ERRORS_MODAL_INFO,
    SHOW_ERRORS_MODAL,
  } from '@/store/mutation-types';

  import spinner from '@/components/widgets/Spinner';

  export default {
    name: 'accountCreationAdmin',
    beforeMount() {
      this.initializeState();
    },
    components: {
      spinner,
      RestrictedSingleInput,
      errors: Errors,
    },
    data() {
      return {
        name: '',
        userId: '',
        user: {},
        accountId: '',
        accountType: '',
        accountName: '',
        interestPayment: '',
        principalPayment: '',
        isLoading: false,
        accountNameChanged: false,
      };
    },
    computed: {
      ...mapState('errors', [
        'isVisibleErrorsModal',
      ]),
      isFromProhibitedState() {
        return (
          ProhibitedStates.find(
            s =>
              this.user.residentialAddress &&
              s === this.user.residentialAddress.state,
          ) !== undefined
        );
      },
      paymentPreference() {
        return this.isFromProhibitedState
        ? [
          paymentPreferenceAction.cashout,
          paymentPreferenceAction.donate,
        ] : [
          paymentPreferenceAction.reinvest,
          paymentPreferenceAction.donate,
          paymentPreferenceAction.cashout,
        ];
      },
      interestPaymentSelected: {
        get() {
          return this.interestPayment ? this.interestPayment : this.paymentPreference[0].name;
        },
        set(preference) {
          this.interestPayment = preference;
        },
      },
      principalPaymentSelected: {
        get() {
          return this.principalPayment ? this.principalPayment : this.paymentPreference[0].name;
        },
        set(preference) {
          this.principalPayment = preference;
        },
      },
    },
    methods: {
      ...mapActions('accountCreation', [
        'getUserDetail',
        'createAccountByAdmin',
      ]),
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      initializeState() {
        this.userId = this.$route.params.userId;
        this.name = this.$route.params.name;
        this.accountId = this.$route.params.accountId;
        this.accountType = this.$route.params.accountType;
        this.loadUserDetail();
      },
      async loadUserDetail() {
        this.isLoading = true;
        await this.getUserDetail(this.userId).then((data) => {
          this.user = Object.assign({}, data);
          this.isLoading = false;
        }).catch(() => {
          this.isLoading = false;
        });
      },
      isSubmitButtonDisabled() {
        if (!this.interestPaymentSelected) {
          return true;
        }
        if (!this.principalPaymentSelected) {
          return true;
        }
        const length = this.accountName.trim().length;

        if (length === 0) {
          return true;
        }
        return false;
      },
      submitRequest() {
        this.isLoading = true;

        const accountObject = {
          paymentPreference: this.principalPaymentSelected,
          interestPreference: this.interestPaymentSelected,
          accountName: this.accountName,
        };

        if (this.accountType.includes('joint')) {
          accountObject.accountType = 'JOINT_ACCOUNT';
        } else if (this.accountType.includes('utma-ugma')) {
          accountObject.accountType = 'UTMA_UGMA_ACCOUNT';
        }

        return this.createAccountByAdmin({
          userId: this.userId,
          accountObject,
        })
          .then(() => {
            this.isLoading = false;
            this.goBack();
          })
          .catch((err) => {
            this.isLoading = false;
            if (err !== 'UNAUTHORIZED') {
              this.setErrorsModalInfo(['Error when creating an account.']);
              this.showErrorsModal();
            }
          });
      },
      goBack() {
        const name = this.name;
        const userId = this.userId;
        if (this.user.userType === UserTypes.FINANCIAL_ADVISOR.name) {
          this.$router.push({ name: 'EditUserFinancial', params: { name, userId } });
        } else {
          this.$router.push({ name: 'UserDetail', params: { name, userId } });
        }
      },
      onAccountNameUpdate(value) {
        this.accountNameChanged = true;
        this.accountName = value.trim();
      },
    },
  };
</script>

<style scoped>
  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  .left-margin {
    margin-left: 4%;
  }
  .right-margin {
    margin-left: -4%;
  }
  .top-margin {
    margin-top: 2%;
  }
  .bottom-margin {
    margin-bottom: 1%;
  }
  .space {
    margin-top: 4%;
  }
  .content-heading {
    font-weight: normal;
  }
  .panel-title {
    font-size: 20px;
  }
  .panel-subtitle {
    font-size: 16px;
  }
  .panel-default {
    padding: 30px 40px;
    width: 750px;
  }
  .panel-heading {
    background-color: #ffffff;
    font-size: 16px;
    padding: 0px 5px;
  }
  label {
    color: #888;
    font-size: 14px;
    width: 250px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0px;
  }
  select:required:invalid {
  color: lightgray;
}
option[value=""][disabled] {
  display: none;
}
option {
  color: black;
}
  .total-amount {
    font-size: 20px;
  }
  .button-approve {
    background-color: #127285;
  }
  .button-white {
    background-color: white;
    border-color: #127285;
    color: #127285;
  }
  .btn-back {
    color: #31b0d5;
    background-color: Transparent;
    background-repeat: no-repeat;
    border: none;
    cursor: pointer;
    overflow: hidden;
    outline: none;
    padding-left: 20px;
  }
  .icon-back {
    position: relative;
  }
  .icon-back:before {
    content: "\f060";
    font-family: FontAwesome;
    position: absolute;
    left: 0px;
  }
  ::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: lightgray;
    opacity: 1; /* Firefox */
}

:-ms-input-placeholder { /* Internet Explorer 10-11 */
    color: lightgray;
}

::-ms-input-placeholder { /* Microsoft Edge */
    color: lightgray;
}
</style>



// WEBPACK FOOTER //
// AccountCreationAdmin.vue?1511efa3